import { pickT } from "@repo/shared";
import { VisualizationContainer } from "@/components/flow/visualization-container";
import { useInstanceExpectedValueSnapshot } from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import { ExpectedValueWidget } from "../expected-value-widget";
import { T } from "./global-expected-value.i18n";

type GlobalExpectedValueProps = {
  instanceId: string;
  config: {
    style?: string;
    title?: string;
    description?: string;
    showChart?: boolean;
    showSummaryTable?: boolean;
    showYearlyTable?: boolean;
    compareWithBank?: boolean;
  };
  language: string;
  /**
   * Engine reported `kind: "snapshot"` for this component (no goalId,
   * snapshotType="instance_expected_value"). When set AND the snapshot row is
   * missing, replace the muted "not available" fallback with a red-bordered
   * placeholder pointing at the regenerate banner. Mirrors GlobalCostTable.
   */
  hasValidationError?: boolean;
};

/**
 * Instance-level (aggregate) expected value visualization for the internal
 * advisor wizard. Unlike the goal-scoped `ExpectedValue`, this reads the
 * `instance_expected_value` snapshot — the sum of all goals' deposits and all
 * wealth-plan withdrawals — so withdrawals and multi-goal deposits are reflected.
 */
export function GlobalExpectedValue({
  instanceId,
  config,
  language,
  hasValidationError = false,
}: GlobalExpectedValueProps) {
  const t = pickT(T, language);
  const { data, isLoading } = useInstanceExpectedValueSnapshot(instanceId);

  if (isLoading) return null;

  if (!data) {
    if (hasValidationError) {
      return (
        <div className="text-sm text-destructive border border-destructive rounded-lg p-4">
          {t.snapshotMissingMessage}
        </div>
      );
    }
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.notAvailable}
      </div>
    );
  }

  const snapshot =
    typeof data === "string"
      ? (() => {
          try {
            return JSON.parse(data as string);
          } catch {
            return null;
          }
        })()
      : data;

  if (!snapshot) {
    return <div className="text-sm text-destructive">{t.errorLoading}</div>;
  }

  if (snapshot.error) {
    return (
      <div className="border border-destructive/50 rounded-lg p-4 space-y-2">
        <div className="text-sm font-medium text-destructive">{t.unavailable}</div>
        <div className="text-xs text-muted-foreground">{String(snapshot.error)}</div>
        <div className="text-xs text-muted-foreground">{t.contactAdvisor}</div>
      </div>
    );
  }

  return (
    <VisualizationContainer config={config}>
      <ExpectedValueWidget
        snapshot={snapshot}
        showChart={config.showChart !== false}
        showSummaryTable={config.showSummaryTable === true}
        showYearlyTable={config.showYearlyTable === true}
        compareWithBank={config.compareWithBank === true}
        language={language}
      />
    </VisualizationContainer>
  );
}
